import { Injectable } from '@angular/core';
import { Actions, Effect, ofType } from '@ngrx/effects';
import { catchError, map, mergeMap } from 'rxjs/operators';
import { forkJoin, of } from 'rxjs';
import { Store } from '@ngrx/store';
import { AppState } from '../../../../core/reducers';
import { DocumentService } from '../services';
import {
  CancelDocumentPage,
  ClearDocumentError,
  CreateDocument,
  CreateDocumentOnServer,
  LoadDocumentPage,
  DocumentActionTypes,
  RequestDocumentPage,
  ThrowDocumentError,
  ToggleDocumentActionLoading,
  ToggleDocumentPageLoading,
  UpdateDocument,
  UpdateDocumentOnServer,
} from '../actions';

@Injectable()
export class DocumentEffects {
  showPageLoading = new ToggleDocumentPageLoading({ pageLoading: true });
  hidePageLoading = new ToggleDocumentPageLoading({ pageLoading: false });
  showActionLoading = new ToggleDocumentActionLoading({ actionLoading: true });
  hideActionLoading = new ToggleDocumentActionLoading({ actionLoading: false });

  constructor(
    private actions$: Actions,
    private store: Store<AppState>,
    private documentService: DocumentService,
  ) {}

  @Effect()
  loadDocumentPage$ = this.actions$.pipe(
    ofType<RequestDocumentPage>(DocumentActionTypes.RequestDocumentPage),
    map((action: RequestDocumentPage) => action.payload),
    mergeMap(({ query }) => {
      this.store.dispatch(this.showPageLoading);
      const queryResult$ = this.documentService.getDocumentPage(query);
      const observables = [queryResult$, of(query)];

      return forkJoin(observables).pipe(
        catchError((err) => of(err)),
        map((res) => {
          if (res.length) {
            this.store.dispatch(new ClearDocumentError());
            this.store.dispatch(this.hidePageLoading);

            return new LoadDocumentPage({
              documents: res[0].data,
              totalCount: res[0].count,
              query: res[1],
            });
          }

          this.store.dispatch(new CancelDocumentPage());

          return new ThrowDocumentError({ error: res });
        }),
      );
    }),
  );

  @Effect()
  createDocument$ = this.actions$.pipe(
    ofType<CreateDocumentOnServer>(DocumentActionTypes.CreateDocumentOnServer),
    map((action: CreateDocumentOnServer) => action.payload),
    mergeMap(({ document, close }) => {
      this.store.dispatch(this.showActionLoading);

      return this.documentService.createDocument(document).pipe(
        catchError((err) => of(err)),
        map((res) => {
          if (res.status === 201) {
            close({ document });

            this.store.dispatch(new ClearDocumentError());
            this.store.dispatch(this.hideActionLoading);

            return new CreateDocument({ document: res.body?.data });
          }

          close();

          return new ThrowDocumentError({ error: res });
        }),
      );
    }),
  );

  @Effect()
  updateDocument$ = this.actions$.pipe(
    ofType<UpdateDocumentOnServer>(DocumentActionTypes.UpdateDocumentOnServer),
    map((action: UpdateDocumentOnServer) => action.payload),
    mergeMap(({ document, close }) => {
      this.store.dispatch(this.showActionLoading);

      return this.documentService.updateDocument(document).pipe(
        catchError((err) => of(err)),
        map((res) => {
          if (res.status === 200) {
            close({ document });

            this.store.dispatch(new ClearDocumentError());
            this.store.dispatch(this.hideActionLoading);

            return new UpdateDocument({ document: res.body?.data });
          }

          close();

          return new ThrowDocumentError({ error: res });
        }),
      );
    }),
  );
}
